import { useEffect, useState } from "react";
import {
    ArrowDownward,
    ArrowUpward,
    BookmarkBorder,
    ChevronRight,
    EditNote,
    History,
    KeyboardArrowDown,
    KeyboardArrowUp,
    Podcasts,
    RecordVoiceOver,
    SystemUpdateAlt,
} from "@mui/icons-material";
import { Modal, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import SectionActionButton from "./SectionActionButton";
import {
    LIBRARY_ACTION_COLLAPSED_COUNT_V1,
    LIBRARY_ACTION_ORDER_V1,
} from "../constants/local_storage_constants";

const DEFAULT_COLLAPSED_ACTION_COUNT = 2;
const MORE_ROW_ID = 'more';
const DEFAULT_ACTION_ORDER = [
    'saved',
    'importShows',
    'played',
    'notes',
    'shows',
    'speakers',
];

const LIBRARY_ACTIONS = {
    saved: {
        Icon: BookmarkBorder,
        title: 'Saved',
        path: '/saved',
    },
    played: {
        Icon: History,
        title: 'Played',
        path: '/played',
    },
    notes: {
        Icon: EditNote,
        title: 'Notes',
        path: '/notes',
    },
    importShows: {
        Icon: SystemUpdateAlt,
        title: 'Import shows',
        path: '/import_shows',
    },
    shows: {
        Icon: Podcasts,
        title: 'Shows',
        path: '/settings/shows',
    },
    speakers: {
        Icon: RecordVoiceOver,
        title: 'Speakers',
        path: '/settings/speakers',
    },
};

const normalizeActionOrder = (candidateOrder) => {
    if (!Array.isArray(candidateOrder)) {
        return [...DEFAULT_ACTION_ORDER];
    }

    const knownIds = new Set(DEFAULT_ACTION_ORDER);
    const seenIds = new Set();
    const normalizedOrder = [];

    candidateOrder.forEach((actionId) => {
        if (knownIds.has(actionId) && !seenIds.has(actionId)) {
            normalizedOrder.push(actionId);
            seenIds.add(actionId);
        }
    });

    DEFAULT_ACTION_ORDER.forEach((actionId) => {
        if (!seenIds.has(actionId)) {
            normalizedOrder.push(actionId);
        }
    });

    return normalizedOrder;
};

const clampCollapsedActionCount = (candidateCount) => {
    const numericCount = Number(candidateCount);
    if (!Number.isFinite(numericCount)) {
        return DEFAULT_COLLAPSED_ACTION_COUNT;
    }

    return Math.min(
        DEFAULT_ACTION_ORDER.length,
        Math.max(1, Math.trunc(numericCount)),
    );
};

const readStoredActionOrder = () => {
    try {
        const storedOrder = window.localStorage.getItem(LIBRARY_ACTION_ORDER_V1);
        if (storedOrder === null) {
            return [...DEFAULT_ACTION_ORDER];
        }
        return normalizeActionOrder(JSON.parse(storedOrder));
    } catch (_) {
        return [...DEFAULT_ACTION_ORDER];
    }
};

const readStoredCollapsedActionCount = () => {
    try {
        const storedCount = window.localStorage.getItem(LIBRARY_ACTION_COLLAPSED_COUNT_V1);
        return storedCount === null
            ? DEFAULT_COLLAPSED_ACTION_COUNT
            : clampCollapsedActionCount(storedCount);
    } catch (_) {
        return DEFAULT_COLLAPSED_ACTION_COUNT;
    }
};

const persistActionOrder = (actionOrder) => {
    try {
        window.localStorage.setItem(
            LIBRARY_ACTION_ORDER_V1,
            JSON.stringify(actionOrder),
        );
    } catch (_) {
        // The in-memory preference remains usable when local storage is unavailable.
    }
};

const persistCollapsedActionCount = (collapsedActionCount) => {
    try {
        window.localStorage.setItem(
            LIBRARY_ACTION_COLLAPSED_COUNT_V1,
            String(collapsedActionCount),
        );
    } catch (_) {
        // The in-memory preference remains usable when local storage is unavailable.
    }
};

const clearStoredLibraryCustomization = () => {
    try {
        window.localStorage.removeItem(LIBRARY_ACTION_ORDER_V1);
        window.localStorage.removeItem(LIBRARY_ACTION_COLLAPSED_COUNT_V1);
    } catch (_) {
        // Reset still applies for the current session when local storage is unavailable.
    }
};

const LibraryHeading = () => {
    const navigate = useNavigate();
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const [isExpanded, setIsExpanded] = useState(false);
    const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
    const [isShowingMoreRowsHint, setIsShowingMoreRowsHint] = useState(false);
    const [actionOrder, setActionOrder] = useState(readStoredActionOrder);
    const [collapsedActionCount, setCollapsedActionCount] = useState(
        readStoredCollapsedActionCount,
    );

    const orderedActionItems = actionOrder.map((actionId) => ({
        id: actionId,
        ...LIBRARY_ACTIONS[actionId],
    }));
    const visibleActionItems = isExpanded
        ? orderedActionItems
        : orderedActionItems.slice(0, collapsedActionCount);
    const hasActionFooter = orderedActionItems.length > 0;
    const hasHiddenActions = collapsedActionCount < orderedActionItems.length;
    const showCustomize = hasActionFooter && (isExpanded || !hasHiddenActions);
    const customizeRowIds = [
        ...actionOrder.slice(0, collapsedActionCount),
        MORE_ROW_ID,
        ...actionOrder.slice(collapsedActionCount),
    ];
    const isDefaultCustomization = (
        collapsedActionCount === DEFAULT_COLLAPSED_ACTION_COUNT
        && actionOrder.every((actionId, index) => actionId === DEFAULT_ACTION_ORDER[index])
    );

    useEffect(() => {
        if (reduceMotion) {
            setIsShowingMoreRowsHint(false);
        }
    }, [reduceMotion]);

    const updateCustomizeRows = (nextRowIds) => {
        const nextMoreIndex = nextRowIds.indexOf(MORE_ROW_ID);
        if (nextMoreIndex < 0) {
            return;
        }

        const actionCountBeforeMore = nextRowIds
            .slice(0, nextMoreIndex)
            .filter((rowId) => DEFAULT_ACTION_ORDER.includes(rowId))
            .length;
        if (actionCountBeforeMore < 1) {
            return;
        }

        const nextCollapsedActionCount = clampCollapsedActionCount(
            actionCountBeforeMore,
        );
        const nextActionOrder = normalizeActionOrder(
            nextRowIds.filter((rowId) => rowId !== MORE_ROW_ID),
        );

        setActionOrder(nextActionOrder);
        setCollapsedActionCount(nextCollapsedActionCount);
        persistActionOrder(nextActionOrder);
        persistCollapsedActionCount(nextCollapsedActionCount);
    };

    const canMoveCustomizeRow = (rowId, offset) => {
        const currentIndex = customizeRowIds.indexOf(rowId);
        const nextIndex = currentIndex + offset;
        if (
            currentIndex < 0
            || nextIndex < 0
            || nextIndex >= customizeRowIds.length
        ) {
            return false;
        }

        const nextRowIds = [...customizeRowIds];
        [nextRowIds[currentIndex], nextRowIds[nextIndex]] = [
            nextRowIds[nextIndex],
            nextRowIds[currentIndex],
        ];

        return nextRowIds.indexOf(MORE_ROW_ID) >= 1;
    };

    const moveCustomizeRow = (rowId, offset) => {
        if (!canMoveCustomizeRow(rowId, offset)) {
            return;
        }

        const currentIndex = customizeRowIds.indexOf(rowId);
        const nextIndex = currentIndex + offset;
        const nextRowIds = [...customizeRowIds];
        [nextRowIds[currentIndex], nextRowIds[nextIndex]] = [
            nextRowIds[nextIndex],
            nextRowIds[currentIndex],
        ];
        updateCustomizeRows(nextRowIds);
    };

    const resetCustomization = () => {
        setActionOrder([...DEFAULT_ACTION_ORDER]);
        setCollapsedActionCount(DEFAULT_COLLAPSED_ACTION_COUNT);
        clearStoredLibraryCustomization();
    };

    return (
        <div className='libraryShortcuts'>
            <h1 className='chatTitle libraryTitle'>Library</h1>

            <div
                className='libraryActionCard'
                onAnimationEnd={(event) => {
                    if (event.animationName === 'libraryMoreRowsHint') {
                        setIsShowingMoreRowsHint(false);
                    }
                }}
            >
                {visibleActionItems.map((item, index) => {
                    const Icon = item.Icon;
                    const shouldShowDivider = (
                        index !== visibleActionItems.length - 1
                        || hasActionFooter
                    );

                    return (
                        <div
                            key={item.id}
                            className='libraryActionRowWrapper'
                            data-action-id={item.id}
                        >
                            <button
                                type='button'
                                className={`libraryActionRow${isShowingMoreRowsHint && index >= collapsedActionCount ? ' libraryActionRowMoreHint' : ''}`}
                                onClick={() => {
                                    setIsShowingMoreRowsHint(false);
                                    navigate(item.path);
                                }}
                            >
                                <span className='libraryActionIconShell'>
                                    <Icon style={{ fontSize: '22px' }} />
                                </span>
                                <span className='libraryActionLabel'>
                                    {item.title}
                                </span>
                                <span className='libraryActionChevronShell'>
                                    <ChevronRight style={{ fontSize: '18px' }} />
                                </span>
                            </button>
                            {shouldShowDivider && <div className='libraryActionDivider' />}
                        </div>
                    );
                })}

                {hasActionFooter && (
                    <div className={`libraryActionFooter${showCustomize ? ' libraryActionFooterWithCustomize' : ''}`}>
                        {hasHiddenActions && <button
                            type='button'
                            className='libraryActionToggleRow'
                            onClick={() => {
                                setIsShowingMoreRowsHint(false);
                                setIsExpanded((currentValue) => !currentValue);
                            }}
                            aria-label={isExpanded ? 'Less' : 'More'}
                            aria-expanded={isExpanded}
                        >
                            <span className='libraryActionTogglePill'>
                                <span className='libraryActionIconShell libraryActionToggleIconShell'>
                                    {isExpanded
                                        ? <KeyboardArrowUp />
                                        : <KeyboardArrowDown />}
                                </span>
                                <span className='libraryActionToggleLabel'>
                                    {isExpanded ? 'Less' : 'More'}
                                </span>
                            </span>
                        </button>}
                        {showCustomize && (
                            <SectionActionButton
                                className='libraryActionCustomizeButton'
                                aria-label='Edit Library layout'
                                onClick={() => {
                                    setIsShowingMoreRowsHint(false);
                                    // Keep layout editing reachable if a previously all-visible list gains hidden rows.
                                    setIsExpanded(true);
                                    setIsCustomizeOpen(true);
                                }}
                            >
                                Edit layout
                            </SectionActionButton>
                        )}
                    </div>
                )}
            </div>

            <Modal
                open={isCustomizeOpen}
                onClose={() => setIsCustomizeOpen(false)}
                className='libraryCustomizeModal'
                aria-labelledby='libraryCustomizeTitle'
                slotProps={{
                    backdrop: {
                        className: 'libraryCustomizeBackdrop',
                    },
                }}
            >
                <section
                    className='libraryCustomizeSheet'
                    role='dialog'
                    aria-modal='true'
                    aria-labelledby='libraryCustomizeTitle'
                >
                    <header className='libraryCustomizeHeader'>
                        <h2 id='libraryCustomizeTitle'>Edit layout</h2>
                        <button
                            type='button'
                            className='libraryCustomizeCloseButton'
                            onClick={() => {
                                setIsCustomizeOpen(false);
                                setIsShowingMoreRowsHint(hasHiddenActions && !reduceMotion);
                            }}
                        >
                            Done
                        </button>
                    </header>

                    <div className='libraryCustomizeScrollArea'>
                        <div className='libraryCustomizeListIntro'>
                            <p id='libraryCustomizeListDescription'>
                                Use the arrows to move shortcuts.
                            </p>
                        </div>

                        <h3 className='libraryCustomizeSectionTitle'>Always shown</h3>
                        <ol
                            className='libraryCustomizeActionList'
                            aria-label='Library shortcuts'
                            aria-describedby='libraryCustomizeListDescription'
                        >
                            {customizeRowIds.map((rowId, rowIndex) => {
                                const isMoreRow = rowId === MORE_ROW_ID;
                                if (isMoreRow) {
                                    return (
                                        <li
                                            key={MORE_ROW_ID}
                                            className='libraryCustomizeActionItem libraryCustomizeMoreRow'
                                            data-library-row-id={MORE_ROW_ID}
                                            aria-label={`More boundary, after ${collapsedActionCount} of ${orderedActionItems.length} shortcuts`}
                                            aria-describedby='libraryCustomizeListDescription libraryCustomizeMoreDescription'
                                        >
                                            <div className='libraryCustomizeMoreContent'>
                                                <span className='libraryCustomizeActionLabel'>
                                                    More
                                                </span>
                                                <span
                                                    id='libraryCustomizeMoreDescription'
                                                    className='libraryCustomizeMoreDescription'
                                                >
                                                    Shortcuts below appear when you tap More.
                                                </span>
                                            </div>
                                        </li>
                                    );
                                }

                                const item = {
                                    id: rowId,
                                    ...LIBRARY_ACTIONS[rowId],
                                };
                                const Icon = item.Icon;

                                return (
                                    <li
                                        key={item.id}
                                        className={[
                                            'libraryCustomizeActionItem',
                                            customizeRowIds[rowIndex + 1] === MORE_ROW_ID
                                                ? 'libraryCustomizeActionItemBeforeMore'
                                                : '',
                                        ].join(' ')}
                                        data-library-row-id={item.id}
                                    >
                                        <div className='libraryCustomizeActionItemContent'>
                                            <span className='libraryActionIconShell'>
                                                <Icon style={{ fontSize: '20px' }} />
                                            </span>
                                            <span className='libraryCustomizeActionCopy'>
                                                <span className='libraryCustomizeActionLabel'>
                                                    {item.title}
                                                </span>
                                            </span>
                                            <div className='libraryCustomizeMoveButtons'>
                                                <button
                                                    type='button'
                                                    onClick={() => moveCustomizeRow(item.id, -1)}
                                                    disabled={!canMoveCustomizeRow(item.id, -1)}
                                                    aria-label={`Move ${item.title} up`}
                                                >
                                                    <ArrowUpward />
                                                </button>
                                                <button
                                                    type='button'
                                                    onClick={() => moveCustomizeRow(item.id, 1)}
                                                    disabled={!canMoveCustomizeRow(item.id, 1)}
                                                    aria-label={`Move ${item.title} down`}
                                                >
                                                    <ArrowDownward />
                                                </button>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>

                        <button
                            type='button'
                            className='libraryCustomizeResetButton'
                            onClick={resetCustomization}
                            disabled={isDefaultCustomization}
                        >
                            Reset layout
                        </button>
                    </div>
                </section>
            </Modal>
        </div>
    );
};

export default LibraryHeading;
